import 'dayjs/locale/nl';
import 'forms/helpers/dateTransform';
import 'site/googleTranslateFix';
import 'site/translations';
import 'site/yupTranslations';

import { AccountEntitlement, Locale, useAccountLazyQuery } from 'generated/gql';
import { i18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { PageLoader } from 'components/PageLoader/PageLoader';
import { PaywallUnknownUser } from 'components/PaywallUnknownUser/PaywallUnknownUser';
import type { Account } from 'generated/gql';
import { stripFromUrl } from 'helpers/stripFromUrl';
import { AccountContextProvider, useEntitlements } from 'hooks/useAccount';
import { useRouter } from 'next/router';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useCookie } from 'react-use';
import type { AuthorizationParams } from '@auth0/auth0-react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  AUTH_REDIRECT_ERROR_DESCRIPTION_KEY,
  AUTH_REDIRECT_ERROR_KEY,
} from '../constants/appConstants';

interface WithAuthProps {
  children: ReactNode;
  isRequired?: boolean;
}

const useAuthorize = () => {
  const { loginWithRedirect } = useAuth0();
  const [, setMagicLink] = useCookie('magic_link');
  /** todo: check if this is still needed */
  return useCallback(
    (params: AuthorizationParams = {}) => {
      let authorizationParams: AuthorizationParams = {
        acr_values: 'urn:els:auth:usernamepwd',
        ...params,
      };
      if (params.magicLink) {
        setMagicLink(params.magicLink);
        const redirect_uri = stripFromUrl(window.location.href, 'token', 'magic_link');
        authorizationParams = {
          ...authorizationParams,
          magic_link: params.magicLink,
          returnTo: redirect_uri || window.location.origin,
        };
      }
      loginWithRedirect({ authorizationParams });
    },
    [loginWithRedirect, setMagicLink],
  );
};

export const WithAuth = ({ children, isRequired }: WithAuthProps) => {
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0();

  // const isLoggedIn = useAuthIsLoggedIn();
  // const isInitialized = useAuthInitialized();
  const [accountQuery] = useAccountLazyQuery();
  // const { value: claims } = useAuthAccessClaims() as { value?: { anon?: boolean } };
  const router = useRouter();
  const { pingPrompt, magic_link } = router.query;
  const [magicLink, setMagicLink] = useCookie('magic_link');
  const authorize = useAuthorize();

  // Determine if the current user is logged in anonymously
  // const isAnonymous = claims?.anon;

  // Contains the current user details.
  const [account, setAccount] = useState<Account | undefined>(undefined);

  const refetchAccount = useCallback(() => {
    accountQuery({
      fetchPolicy: 'network-only',
      onCompleted: ({ account }) => {
        if (account) setAccount(account);
      },
    });
  }, [accountQuery]);

  // Update the user details from the backend.
  useEffect(() => {
    if (isAuthenticated && !account) {
      accountQuery({
        onCompleted: ({ account }) => {
          if (account) {
            setAccount(account);
          }
        },
      });
    }
    if (account) {
      const localeString = account.locale === Locale.EnglishBritish ? 'en-gb' : 'nl';
      i18n.activate(localeString);
    }
  }, [accountQuery, account, isAuthenticated]);

  useEffect(() => {
    // Authentication is still initializing
    if (isLoading) {
      return;
    }

    if (typeof magic_link === 'string' && magicLink !== magic_link) {
      authorize({ magicLink: magic_link });
      return;
    }

    // Not logged in? Start the authentication process.
    if (!isAuthenticated) {
      if (!isRequired) return;

      const queryParams = new URLSearchParams(window.location.search);

      const isErrorState =
        queryParams.get(AUTH_REDIRECT_ERROR_KEY) &&
        queryParams.get(AUTH_REDIRECT_ERROR_DESCRIPTION_KEY);

      if (isErrorState) {
        router.push({
          pathname: '/error',
          query: {
            [AUTH_REDIRECT_ERROR_KEY]: queryParams.get(AUTH_REDIRECT_ERROR_KEY),
            [AUTH_REDIRECT_ERROR_DESCRIPTION_KEY]: queryParams.get(
              AUTH_REDIRECT_ERROR_DESCRIPTION_KEY,
            ),
          },
        });
        return;
      }

      // Allow Cypress to use EmbeddedLogin and bypass the Ping prompt.
      if (pingPrompt === 'none') {
        authorize({ prompt: 'none' });
      } else if (magicLink) {
        authorize({ magicLink });
      } else {
        // authorize();
        router.push({ pathname: '/login' });
      }

      return;
    }

    // // Logged in but anonymous? Start the authentication process.
    // if (isAnonymous) {
    //   if (!isRequired) return;
    //   authorize({ prompt: 'login', acr_values: 'urn:els:auth:usernamepwd' });
    // }
  }, [
    authorize,
    // isAnonymous,
    isLoading,
    isAuthenticated,
    isRequired,
    magicLink,
    magic_link,
    pingPrompt,
    setMagicLink,
    loginWithRedirect,
  ]);

  if (isLoading) {
    return <PageLoader />;
  }

  if (!isRequired) {
    return (
      <AccountContextProvider value={{ account, refetchAccount }}>
        <I18nProvider i18n={i18n}>{children}</I18nProvider>
      </AccountContextProvider>
    );
  }

  if (!isAuthenticated) {
    return <PageLoader />;
  }

  if (!account) {
    return <PageLoader />;
  }

  return (
    <AccountContextProvider value={{ account, refetchAccount }}>
      <I18nProvider i18n={i18n}>
        <PaywallProvider>{children}</PaywallProvider>
      </I18nProvider>
    </AccountContextProvider>
  );
};

const PaywallProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [, hasEvidendSubscription] = useEntitlements([AccountEntitlement.ViewEvidend]);

  if (!hasEvidendSubscription) {
    return <PaywallUnknownUser />;
  }
  return <>{children}</>;
};
